
// This file is automatically generated. Do not edit it directly.
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';
import { formatPsychologistData, formatPsychologistsData } from '@/utils/scheduleUtils';

const SUPABASE_URL = "https://olpuhxpkqkwojdoricuo.supabase.co";
const SUPABASE_PUBLISHABLE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im9scHVoeHBrcWt3b2pkb3JpY3VvIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDc3ODM1MjIsImV4cCI6MjA2MzM1OTUyMn0.70DnDLI7tgHjjPxpOttq4ZGD3-iFTzHR6NO8VeHZG10";

// Create the base Supabase client with improved configuration
export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storage: window.localStorage,
    flowType: 'pkce'
  },
  global: {
    headers: {
      'X-Client-Info': 'psyonline-webapp'
    }
  }
});

// Helper function to book an appointment
export const bookAppointment = async (
  psychologistId: string,
  userId: string,
  date: string,
  time: string,
  duration: number,
  price: number
) => {
  try {
    const { data, error } = await supabase
      .from('appointments')
      .insert({
        psychologist_id: psychologistId,
        user_id: userId,
        date,
        time,
        duration,
        total_price: price,
        status: 'pending',
        payment_status: 'pending'
      })
      .select()
      .single();
      
    if (error) throw error;
    return { success: true, appointment: data };
  } catch (error) {
    console.error('Error booking appointment:', error);
    return { success: false, error };
  }
};

// Helper function to get psychologists
export const getPsychologists = async () => {
  try {
    const { data, error } = await supabase
      .from('psychologists')
      .select('*, is_approved, languages');
      
    if (error) throw error;
    
    const formattedData = formatPsychologistsData(data);
    return { success: true, psychologists: formattedData };
  } catch (error) {
    console.error('Error fetching psychologists:', error);
    return { success: false, error, psychologists: [] };
  }
};

// Helper function to get psychologist by ID
export const getPsychologistById = async (id: string) => {
  try {
    const { data, error } = await supabase
      .from('psychologists')
      .select('*, is_approved, languages')
      .eq('id', id)
      .single();
      
    if (error) throw error;
    
    const formattedData = formatPsychologistData(data);
    return { success: true, psychologist: formattedData };
  } catch (error) {
    console.error(`Error fetching psychologist with ID ${id}:`, error);
    return { success: false, error };
  }
};

// Helper function to get available time slots
export const getAvailableTimeSlots = async (psychologistId: string, date: string) => {
  try {
    const { data, error } = await supabase
      .from('time_slots')
      .select('*')
      .eq('psychologist_id', psychologistId)
      .eq('date', date)
      .eq('is_available', true);
      
    if (error) throw error;
    return { success: true, timeSlots: data };
  } catch (error) {
    console.error('Error fetching available time slots:', error);
    return { success: false, error };
  }
};

// Enhanced error handling for auth-related operations
export const handleSupabaseError = (error: any) => {
  console.error('Supabase error:', error);
  
  if (error?.message?.includes('JWT') || error?.message?.includes('auth')) {
    console.warn('Authentication error detected, user may need to re-login');
    // Could trigger a re-authentication flow here
    return { type: 'auth', message: 'Sessão expirada. Faça login novamente.' };
  }
  
  if (error?.message?.includes('refresh_token')) {
    console.warn('Refresh token error detected');
    return { type: 'refresh', message: 'Problema com a autenticação. Tente novamente.' };
  }
  
  return { type: 'general', message: error?.message || 'Erro inesperado' };
};
